import { getSymbol, Input, Select } from "@xola/ui-kit";
import { produce } from "immer";
import React from "react";
import { AdjustmentOperation, AdjustmentType, DemographicPrice, PricingGroup, PricingTerm } from "types";
import { useSeller } from "../../../hooks/useAccount";

interface BuyoutPricingEditorProps {
    error: any;
    pricingGroup: PricingGroup;
    onChange: (terms: PricingTerm[]) => void;
}

const getBuyoutPriceFromPricingGroup = (pricingGroup: PricingGroup) => {
    // Get the price of the first item since all items will have same values
    return pricingGroup.terms?.[0].demographicPrices?.[0] ?? {};
};

export const BuyoutPricingEditor = ({ error, pricingGroup, onChange }: BuyoutPricingEditorProps) => {
    const seller = useSeller();
    const buyoutPrice = getBuyoutPriceFromPricingGroup(pricingGroup);

    const handleChange = (key: keyof DemographicPrice, value?: any) => {
        const updatedPricingGroup = produce(pricingGroup, (draft) => {
            const currentTerm = { ...draft.terms[0] };
            for (const demographicPrice of currentTerm.demographicPrices as any) {
                demographicPrice[key] = value;
            }

            draft.terms[0] = currentTerm;
        });

        onChange([updatedPricingGroup.terms[0]]);
    };

    return (
        <div className="space-y-4">
            <div className="inline-flex flex-grow-0">
                <Input
                    isRequired
                    className="h-9 w-25 rounded-r-none"
                    value={buyoutPrice.adjustmentAmount}
                    type="number"
                    title={error?.adjustmentAmount}
                    onChange={(e: any) =>
                        handleChange(`adjustmentAmount`, e.target.value ? Number(e.target.value) : undefined)
                    }
                />
                <Select
                    disabled={buyoutPrice.adjustmentOperation === AdjustmentOperation.FINAL}
                    className="w-20 rounded-none"
                    value={buyoutPrice.adjustmentType}
                    onChange={(e: any) => handleChange(`adjustmentType`, e.target.value)}
                >
                    <option value={AdjustmentType.ABSOLUTE}>{getSymbol(seller.currency)}</option>
                    <option value={AdjustmentType.PERCENT}>%</option>
                </Select>
                <Select
                    className="mr-4 w-28 rounded-l-none"
                    value={buyoutPrice.adjustmentOperation}
                    onChange={(e: any) => handleChange(`adjustmentOperation`, e.target.value)}
                >
                    <option value={AdjustmentOperation.MARKDOWN}>Markdown</option>
                    <option value={AdjustmentOperation.MARKUP}>Markup</option>
                </Select>
            </div>
            <span className="text-gray-darker">of remaining spots</span>
        </div>
    );
};
